import apiClient from './client';
import { getUserId } from '../analytics';
import {
  buildConsentPayloadFromPreferences,
  getConsentPreferences,
} from '../utils/consentPreferences';

const SYNC_KEY = 'user-sync-last-run';
const SYNC_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
// Universal debounce for profile sync
const DEBOUNCE_MS = 1000; // 1 second in all environments

let debounceTimer = null;

const isObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);

export const normalizeUserProfileResponse = (data) => {
  if (!isObject(data) || !isObject(data.user)) {
    return data;
  }

  return {
    ...data.user,
    app_resources: data.app_resources,
  };
};

// Collect current user profile from localStorage
const collectUserPayload = () => {
  // Convert newsletter timestamp from localStorage
  const newsletterTimestamp = localStorage.getItem('newsletter-signup-completed');
  let newsletterSubscribedAt = null;
  if (newsletterTimestamp) {
    // Convert from timestamp to ISO string
    const timestamp = parseInt(newsletterTimestamp, 10);
    if (!isNaN(timestamp)) {
      newsletterSubscribedAt = new Date(timestamp).toISOString();
    }
  }

  // Infer earliest game start time across saved game states
  let inferredCreatedAt = null;
  try {
    const allKeys = Object.keys(localStorage);
    for (const key of allKeys) {
      // We expect many keys like `<game>_<YYYY-MM-DD>` storing JSON with possible gameStartTime
      // We don't constrain key shape too much; we just try to parse JSON and read top-level gameStartTime
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      if (raw.length > 2 && (raw[0] === '{' || raw[0] === '[')) {
        try {
          const parsed = JSON.parse(raw);
          let start = null;
          if (parsed && typeof parsed === 'object') {
            // Accept either numeric epoch ms or ISO string
            if (typeof parsed.gameStartTime === 'number') {
              start = new Date(parsed.gameStartTime);
            } else if (typeof parsed.gameStartTime === 'string') {
              const d = new Date(parsed.gameStartTime);
              if (!isNaN(d.getTime())) start = d;
            }
          }
          if (start && !isNaN(start.getTime())) {
            if (!inferredCreatedAt || start < inferredCreatedAt) {
              inferredCreatedAt = start;
            }
          }
        } catch (_) {
          // ignore non-JSON values
        }
      }
    }
  } catch (_) {
    // ignore errors reading localStorage
  }

  const createdAtIso = inferredCreatedAt ? inferredCreatedAt.toISOString() : null;
  const consentPayload = buildConsentPayloadFromPreferences(getConsentPreferences());

  return {
    username: localStorage.getItem('username') || null,
    email: localStorage.getItem('email') || null,
    language_level: localStorage.getItem('language-level') || null,
    analytics_consent: consentPayload.analytics_consent,
    functional_consent: consentPayload.functional_consent,
    marketing_consent: consentPayload.marketing_consent,
    consent_version: consentPayload.consent_version,
    consent_updated_at: consentPayload.consent_updated_at,
    consent_source: consentPayload.consent_source,
    newsletter_subscribed_at: newsletterSubscribedAt,
    // Only respected by backend when creating a new identity
    created_at: createdAtIso,
  };
};

// Internal sync function
const doSync = async () => {
  const userId = getUserId();
  if (!userId) return;
  
  try {
    const payload = collectUserPayload();
    await apiClient.put(`/users/me`, payload);
    localStorage.setItem(SYNC_KEY, Date.now().toString());
    console.log('[Users] Profile synced to database');
  } catch (error) {
    console.error('[Users] Failed to sync profile:', error);
    // Don't throw - this is background sync
  }
};

// Bootstrap user profile sync (throttled)
export const bootstrapUserFromLocalStorage = async () => {
  const lastSync = parseInt(localStorage.getItem(SYNC_KEY) || '0', 10);
  const timeSinceLastSync = Date.now() - lastSync;
  
  if (Number.isNaN(lastSync) || timeSinceLastSync >= SYNC_INTERVAL_MS) {
    console.log('[Users] Bootstrapping user profile sync');
    await doSync();
  } else {
    console.log(`[Users] Skipping sync - last sync was ${Math.round(timeSinceLastSync / 1000 / 60)} minutes ago`);
  }
};

// Debounced sync for profile changes
export const syncUserProfileDebounced = () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    console.log('[Users] Debounced profile sync triggered');
    doSync();
  }, DEBOUNCE_MS);
};

// Force an immediate sync (handy for debugging / dev tools)
export const forceSyncUserProfile = async () => {
  await doSync();
};

// Helper to update a profile field and trigger sync
export const updateUserProfileField = (key, value) => {
  localStorage.setItem(key, value);
  syncUserProfileDebounced();
};

// Get user profile from server
export const getUserProfile = async (token = null, params = null) => {
  const userId = getUserId();
  if (!userId) throw new Error('No user ID found');

  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await apiClient.get(`/users/me`, { headers, params: params || undefined });
  return normalizeUserProfileResponse(response.data);
};

// Update user profile on server
export const updateUserProfile = async (updates, token = null) => {
  const userId = getUserId();
  if (!userId) throw new Error('No user ID found');

  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await apiClient.put(`/users/me`, updates, { headers });
  return normalizeUserProfileResponse(response.data);
};

export const redeemRewardClaimTokens = async (tokens, token = null) => {
  const userId = getUserId();
  if (!userId) throw new Error('No user ID found');

  const normalizedTokens = Array.isArray(tokens)
    ? tokens
      .map((item) => (typeof item?.token === 'string' ? item.token.trim() : ''))
      .filter(Boolean)
    : [];

  if (normalizedTokens.length === 0) {
    return null;
  }

  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await apiClient.post(
    '/users/reward-claims/redeem',
    { tokens: normalizedTokens },
    { headers },
  );
  return response.data;
};

export const getPendingGuestRewards = async (token = null) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  return (await apiClient.get('/users/reward-claims/pending', { headers })).data;
};

export const presentGuestRewardOffer = async (context = {}) => (
  (await apiClient.post('/users/reward-offers/present', context)).data
);
